'use client';

import Link from 'next/link';
import { useRef } from 'react';
import { Trans } from 'react-i18next';
import { Accordion, AccordionGroup } from 'ui';
import { LOCALE } from 'utils';

import { useTranslation } from '@/i18n/client';
import { ContactForm } from '@/modules/contact/components/contactForm';
import { ContactCards } from '@/shared/components/contactCards/ContactCards';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';

import styles from './ContactPage.module.scss';

const RETURN_FORM_URL = {
  [LOCALE.SK]: '/assets/Formulár_na_odstúpenie_od_zmluvy_FINESIO.SK.pdf',
  [LOCALE.CZ]: '/assets/finesio-cz-formular-reklamace-vraceni.pdf',
  [LOCALE.RO]: '/assets/finesio-cz-formular-reklamace-vraceni.pdf',
  [LOCALE.PL]: '/assets/formularz-odstapienia-od-umowy-garnolli.pdf',
  [LOCALE.DE]: '/assets/ruckgabe.pdf',
};

export const ContactPage = () => {
  const { t } = useTranslation(['contact', 'common', 'faq']);
  const contactFormRef = useRef<HTMLDivElement>(null);
  const locale = useCurrentLocale();

  return (
    <div className={`${styles.contact__container}`}>
      <div className={styles.contact__overflowWrapper}>
        <div className="container">
          <div className={styles.contact__title}>
            <h2>{t('common:common.contactUs')}</h2>
            <p className={styles['contact__title--description']}>
              {t('common:common.contactForms')}
            </p>
          </div>
        </div>

        <div className={`${styles.contact__rowContainer}`}>
          <div className={`container ${styles.contact__accordionContainer}`}>
            <div className={styles.contact__intro}>
              <h2>{t('faq:faq.title')}</h2>
              <p className={styles.text__gray}>{t('faq:faq.description')}</p>
            </div>
            <div className={styles.contact__accordion}>
              <AccordionGroup>
                <Accordion heading={t('faq:faq.howReturnQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>
                      <Trans
                        i18nKey="faq:faq.howReturnAnswer"
                        t={t}
                        components={[
                          <a
                            href={RETURN_FORM_URL[locale]}
                            target="_blank"
                            rel="noopener noreferrer"
                            key={0}
                          />,
                          <Link
                            href={routes[locale].RETURN_COMPLAINT}
                            key={1}
                          />,
                        ]}
                      />
                    </p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.orderCompletionTimeQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.orderCompletionTimeAnswer')}</p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.howToMakeReturnQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>
                      <Trans
                        i18nKey="faq:faq.howToMakeReturnAnswer"
                        t={t}
                        components={[
                          <a
                            href={`mailto:${t('faq:faq.returnPolicyEmail')}`}
                            key={0}
                          />,
                          <Link
                            href={routes[locale].RETURN_COMPLAINT}
                            key={1}
                          />,
                        ]}
                      />
                    </p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.canICancelQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.canICancelAnswer1')}</p>
                    <p>{t('faq:faq.canICancelAnswer2')}</p>
                  </div>
                </Accordion>
                {locale === LOCALE.PL ? (
                  <Accordion heading={t('faq:faq.phoneOrderQuestion')}>
                    <div className={styles.contact__accordionContent}>
                      <p>
                        <Trans
                          i18nKey="faq:faq.phoneOrderAnswer"
                          t={t}
                          components={[
                            <a
                              href={`tel:${t('common:common.phoneLabel')}`}
                              key={0}
                            />,
                          ]}
                        />
                      </p>
                    </div>
                  </Accordion>
                ) : null}
                <Accordion heading={t('faq:faq.cashReturnQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.cashReturnAnswer')}</p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.shipmentInfoChangeQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.shipmentInfoChangeAnswer')}</p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.invoiceInfoChangeQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.invoiceInfoChangeAnswer1')}</p>
                    <p>{t('faq:faq.invoiceInfoChangeAnswer2')}</p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.personalCollectionQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.personalCollectionAnswer')}</p>
                  </div>
                </Accordion>
                <Accordion heading={t('faq:faq.bankAccountQuestion')}>
                  <div className={styles.contact__accordionContent}>
                    <p>{t('faq:faq.bankAccountAnswer1')}</p>
                    <p>{t('faq:faq.bankAccountAnswer2')}</p>
                    <p>
                      <Trans
                        i18nKey="faq:faq.bankAccountAnswer3"
                        t={t}
                        components={[<br key={0} />]}
                      />
                    </p>
                  </div>
                </Accordion>
              </AccordionGroup>
            </div>
          </div>

          <div className={styles.contact__formContainer} ref={contactFormRef}>
            <ContactForm />
          </div>
        </div>

        <div className={styles.contact__rowContainer}>
          <div className="container">
            <ContactCards contactFormRef={contactFormRef} />
          </div>
        </div>

        <div className="container">
          <div className={styles.contact__rowContainer}>
            <div className={`container ${styles.contact__otherData}`}>
              <h2>{t('contact:otherData.title')}</h2>
              <p>
                <Trans
                  i18nKey="contact:otherData.content"
                  t={t}
                  components={[
                    <br key={0} />,
                    <a
                      href={`mailto:${t('contact:otherData.invoiceEmail')}`}
                      key={1}
                    />,
                    <a
                      href={`mailto:${t('contact:otherData.marketingEmail')}`}
                      key={2}
                    />,
                    <a
                      href={`mailto:${t('contact:otherData.cooperationEmail')}`}
                      key={3}
                    />,
                  ]}
                />
              </p>
            </div>

            <div className={`container ${styles.contact__companyData}`}>
              <h2>{t('contact:companyData.title')}</h2>
              <p>
                <Trans
                  i18nKey="contact:companyData.content"
                  t={t}
                  components={[<br key={0} />]}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
